import React, { useState, useEffect } from 'react';
import { useDN } from '../../contexts/DNContext';
import { API_BASE_URL } from '../../config';
import LoadingSpinner from '../LoadingSpinner';
import { format } from 'date-fns';
import DNControlVariations from './DNControlVariations';
import DNControlReasons from './DNControlReasons';
import DNControlDataStructure from './DNControlDataStructure';
import DNControlEOL from './DNControlEOL';
import { Menu } from '../ui/Menu';

const DNControl = ({ selectedProject, fetchWithAuth, currentUser }) => {
  const { startDate, endDate, products } = useDN();
  const [loading, setLoading] = useState(false);
  const [measuresData, setMeasuresData] = useState([]);
  const [eolProducts, setEolProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('variations');

  // Récupérer toutes les mesures sans filtrer par produit
  useEffect(() => {
    const fetchMeasures = async () => {
      if (!selectedProject?.value) return;

      try {
        setLoading(true);
        const formattedStartDate = format(startDate, 'yyyy-MM-dd');
        const formattedEndDate = format(endDate, 'yyyy-MM-dd');

        const url = `${API_BASE_URL}/api/dwh/dn/${selectedProject.value}/products/measures?startDate=${formattedStartDate}&endDate=${formattedEndDate}&withUsername=${currentUser.isadmin}`;

        const response = await fetchWithAuth(url);

        if (response.ok) {
          const data = await response.json();
          setMeasuresData(data);
        } else {
          console.error('Failed to fetch measures', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching measures:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMeasures();
  }, [selectedProject, startDate, endDate, fetchWithAuth]);

  // Fonction pour récupérer les produits EOL
  const fetchEOLProducts = async () => {
    if (!selectedProject?.value) return;

      try {
        const formattedStartDate = format(startDate, 'yyyy-MM-dd');

        const url = `${API_BASE_URL}/api/dwh/dn/${selectedProject.value}/products/eol?startDate=${formattedStartDate}`;

        const response = await fetchWithAuth(url);

        if (response.ok) {
          const data = await response.json();
          setEolProducts(data);
        } else {
          console.error('Failed to fetch EOL products', response.statusText);
        }
      } catch (error) {
        console.error('Error fetching EOL products:', error);
      }
    };

  // Récupérer les produits EOL
  useEffect(() => {
    fetchEOLProducts();
  }, [selectedProject, startDate, endDate, fetchWithAuth]);

  if (loading)
    return <LoadingSpinner />;

  if (!currentUser.isadmin && !currentUser.issuperadmin) {
    return (
      <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
        <p className="text-gray-500 dark:text-gray-400">Vous n'avez pas les permissions pour accéder à cette page.</p>
      </div>
    );
  }

  if (!selectedProject?.value) {
    return (
      <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
        <p className="text-gray-500 dark:text-gray-400">Veuillez sélectionner un projet pour analyser les données.</p>
      </div>
    );
  }

  // Créer des versions simplifiées des composants pour le cas où il n'y a pas de données
  const EmptyState = () => (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
      <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">Contrôle de Cohérence DN</h2>
      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md">
        <p className="text-blue-700 dark:text-blue-300">
          Aucune incohérence détectée dans les données pour la période sélectionnée.
        </p>
      </div>
    </div>
  );

  // Analyser si on a des données à afficher
  const hasData = measuresData.length > 0;

  const menuItems = [
    {
      id: 'variations',
      label: 'Variations DN',
      icon: (
        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4v16M20 4v16" />
        </svg>
      ),
      badge: <DNControlVariations.Badge measures={measuresData} />
    },
    {
      id: 'mapping',
      label: 'Raisons d\'absence',
      icon: (
        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      ),
      badge: <DNControlReasons.Badge measures={measuresData} />
    },
    {
      id: 'eol-products',
      label: 'Produits EOL',
      icon: (
        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      ),
      badge: <DNControlEOL.Badge eolProducts={eolProducts} />
    },
    {
      id: 'data-structure',
      label: 'Structure des Données',
      icon: (
        <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1 3 3 3h10c2 0 3-1 3-3V7c0-2-1-3-3-3H7c-2 0-3 1-3 3z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7h16" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 11h16" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 15h16" />
        </svg>
      )
    }
  ];

  return (
    <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
      {/* Layout avec sidebar */}
      <div className="flex flex-col md:flex-row gap-6">
        {/* Sidebar */}
        <div className="w-full md:w-60 shrink-0">
          <Menu
            items={menuItems}
            activeTab={activeTab}
            onTabClick={setActiveTab}
          />
        </div>

        {/* Contenu principal */}
        <div className="flex-1">
          {hasData && activeTab === 'variations' && (
            <DNControlVariations measures={measuresData} />
          )}
          {hasData && activeTab === 'mapping' && (
            <DNControlReasons measures={measuresData} />
          )}
          {activeTab === 'eol-products' && (
            <DNControlEOL 
              eolProducts={eolProducts} 
              projectId={selectedProject?.value}
              onRefresh={fetchEOLProducts}
            />
          )}
          {hasData && activeTab === 'data-structure' && (
            <DNControlDataStructure selectedProject={selectedProject} fetchWithAuth={fetchWithAuth} products={products} />
          )}
          {!hasData && (
            <EmptyState />
          )}
        </div>
      </div>
    </div>
  );
};

export default DNControl;